import { useReducer } from 'react';
import { submitQuestionnaireRequest } from '../../assessment-portal/services';
import { RequestStatus } from '../../model';
import { ASSESSMENT_TYPE } from '../data/constants';
import type {
  AdhdQuestionnaireFlowState,
  IdentityConfirmValues,
  QuestionnaireCompletionDisplay,
  QuestionnaireMeta,
} from '../types/flow';
import { parseDobToIso } from '../utils/identity';
import { loadBookingConfirmationSnapshot } from '../utils/booking-confirmation-snapshot';
import {
  buildQuestionnaireCompletionDisplay,
  clearQuestionnaireFlowStorage,
} from '../utils/questionnaire-completion';
import {
  initialSubmissionState,
  submissionReducer,
  toFailure,
} from './submission-state';
import type { SubmissionFailure, SubmissionState } from './submission-state';
import type { QuestionnaireSubmissionPayload } from '../../assessment-portal/services/questionnaire';

export interface QuestionnaireSubmitOverrides {
  answers?: Record<string, unknown>
  questionnaireMeta?: QuestionnaireMeta
}

type BuildPayloadResult =
  | { payload: QuestionnaireSubmissionPayload }
  | { error: SubmissionFailure };

type SubmitQuestionnaireResult =
  | { status: RequestStatus.SUCCESS; completionDisplay: QuestionnaireCompletionDisplay }
  | { status: RequestStatus.FAILURE; failure: SubmissionFailure };

const buildQuestionnairePayload = (
  identity: IdentityConfirmValues,
  questionnaireMeta: QuestionnaireMeta,
  answers: Record<string, unknown>,
): BuildPayloadResult => {
  const isoDob = parseDobToIso(identity.dateOfBirth);
  if (!isoDob) {
    return {
      error: {
        kind: 'validation' as const,
        message: 'Please enter a valid date of birth (DD/MM/YYYY).',
      },
    };
  }

  if (!identity.firstName?.trim()) {
    return {
      error: {
        kind: 'validation' as const,
        message: 'Please enter your first name.',
      },
    };
  }

  if (!identity.lastName?.trim()) {
    return {
      error: {
        kind: 'validation' as const,
        message: 'Please enter your last name.',
      },
    };
  }

  // The server attaches to this workflow only when it is the same patient's.
  const workflowId = loadBookingConfirmationSnapshot()?.workflowId;

  return {
    payload: {
      assessmentType: ASSESSMENT_TYPE as 'ADHD',
      ...(workflowId ? { workflowId } : {}),
      identity: {
        firstName: identity.firstName.trim(),
        lastName: identity.lastName.trim(),
        dateOfBirth: isoDob,
      },
      questionnaire: {
        templateId: questionnaireMeta.templateId,
        templateVersion: questionnaireMeta.templateVersion,
        answers,
      },
    },
  };
};

export const submitQuestionnaireWithIdentity = async (
  identity: IdentityConfirmValues,
  questionnaireMeta: QuestionnaireMeta,
  answers: Record<string, unknown>,
): Promise<SubmitQuestionnaireResult> => {
  const built = buildQuestionnairePayload(identity, questionnaireMeta, answers);
  if ('error' in built) {
    return { status: RequestStatus.FAILURE, failure: built.error };
  }

  const result = await submitQuestionnaireRequest(built.payload);

  if (result.status === RequestStatus.SUCCESS) {
    const completionDisplay = buildQuestionnaireCompletionDisplay(identity, result.data);
    clearQuestionnaireFlowStorage(questionnaireMeta.templateId);
    return { status: RequestStatus.SUCCESS, completionDisplay };
  }

  const errorMsg = result.error || 'Submit questionnaire failed';
  return {
    status: RequestStatus.FAILURE,
    failure: toFailure(result.errorCode, errorMsg),
  };
};

const useQuestionnaireSubmission = (flowState: AdhdQuestionnaireFlowState) => {
  const [state, dispatch] = useReducer(submissionReducer, initialSubmissionState);

  const handleSubmitQuestionnaire = async (
    identity: IdentityConfirmValues,
    onSuccess: (completionDisplay: QuestionnaireCompletionDisplay) => void,
    overrides?: QuestionnaireSubmitOverrides,
  ) => {
    dispatch({ type: 'SUBMIT_STARTED' });

    const questionnaireMeta = overrides?.questionnaireMeta ?? flowState.questionnaireMeta;
    const answers = overrides?.answers ?? flowState.questionnaireAnswers;

    const result = await submitQuestionnaireWithIdentity(identity, questionnaireMeta, answers);

    if (result.status === RequestStatus.SUCCESS) {
      dispatch({ type: 'SUBMIT_SUCCEEDED' });
      onSuccess(result.completionDisplay);
      return;
    }

    dispatch({
      type: 'SUBMIT_FAILED',
      failure: result.failure,
    });
  };

  const resetSubmitError = () => {
    dispatch({ type: 'RESET_ERROR' });
  };

  return {
    state,
    handleSubmitQuestionnaire,
    resetSubmitError,
  };
};

export type { SubmissionState };
export default useQuestionnaireSubmission;
